// Copyright (C) AirWorks Solutions, Inc - All Rights Reserved
// DO NOT REDISTRIBUTE
// UNAUTHORIZED COPYING OF THIS FILE, ANY PART OR WHOLE, VIA ANY MEDIUM IS STRICTLY PROHIBITED
// PROPRIETARY AND CONFIDENTIAL

import { createSelector } from 'reselect';
import type { RootState } from 'Store';
import { tifStem } from 'Utils/fileNameUtils';
import { basename } from 'Utils/fileSort';

export interface TifFootprintMeta {
  gsd: number;
  epsg: number;
  createdAt?: string;
  adminUpload: boolean;
  autonomous?: boolean;
}

export interface LasFootprintMeta {
  points: number;
  density: number;
  epsg: number;
  verticalEpsg?: number;
  createdAt?: Date;
  adminUpload: boolean;
}

export interface PanoFootprintMeta {
  createdAt?: Date;
}

export interface FileFootprint {
  kind: FileSelectionKind;
  id: string;
  name: string;
  stem: string;
  bounds: [number, number, number, number];
  meta: TifFootprintMeta | LasFootprintMeta | PanoFootprintMeta;
}

const selectTifFiles = (state: RootState) => state.project.project?.tifFiles;
const selectRasterTileJson = (state: RootState) => state.map.common.rasterTileJson;
const selectLasFiles = (state: RootState) => state.project.project?.lasFiles;
const selectPanoramaFiles = (state: RootState) => state.project.project?.panoramaFiles;

// Tiler bounds (rasterTileJson, keyed by tifStem) are preferred: they are what
// MapRasterLayer actually draws with. The Mongo bbox is the fallback for tifs
// whose tiles have not landed yet.
export const getTifFootprints = createSelector(
  [selectTifFiles, selectRasterTileJson],
  (tifFiles, rasterTileJson): FileFootprint[] => {
    if (!tifFiles?.length) return [];
    const footprints: FileFootprint[] = [];
    tifFiles.forEach((tif: ITifFile) => {
      const stem = tifStem(tif.filename);
      const bounds = rasterTileJson?.[stem]?.bounds || tif.bbox;
      if (!bounds || bounds.length !== 4) {
        console.warn(`footprints: no bounds for tif ${stem} (no tiler metadata, no bbox); skipping`);
        return;
      }
      footprints.push({
        kind: 'tif',
        id: tif._id,
        name: basename(tif.filename),
        stem,
        bounds,
        meta: {
          gsd: tif.gsd,
          epsg: tif.epsg,
          createdAt: tif.createdAt,
          adminUpload: tif.adminUpload,
          autonomous: tif.autonomous,
        },
      });
    });
    return footprints;
  },
);

// Exactly the point clouds the Data Sets panel lists: input files only. The
// bbox guard mirrors GetLasBBoxThunk (a falsy bbox[0] means lascheck has not
// written real bounds yet).
export const getLasFootprints = createSelector(
  [selectLasFiles],
  (lasFiles): FileFootprint[] => {
    const inputs = lasFiles?.filter((las: ILasFile) => !las.ownerOrder && las.lasType === 'unclassified') || [];
    const footprints: FileFootprint[] = [];
    inputs.forEach((las: ILasFile) => {
      if (!(las.bbox?.length === 4 && !!las.bbox[0])) {
        console.warn(`footprints: no bbox for las ${basename(las.filename)}; skipping`);
        return;
      }
      footprints.push({
        kind: 'las',
        id: las._id,
        name: basename(las.filename),
        stem: basename(las.filename),
        bounds: las.bbox,
        meta: {
          points: las.points,
          density: las.density,
          epsg: las.epsg,
          verticalEpsg: las.verticalEpsg,
          createdAt: las.createdAt,
          adminUpload: las.adminUpload,
        },
      });
    });
    return footprints;
  },
);

// Pano zips whose panoUnzip run reported a coverage bbox (D76); older uploads
// have an empty array and simply draw nothing.
export const getPanoFootprints = createSelector(
  [selectPanoramaFiles],
  (panoramaFiles): FileFootprint[] => {
    const footprints: FileFootprint[] = [];
    (panoramaFiles || []).forEach((pano: IPanoramaFile) => {
      if (!(pano.bbox?.length === 4 && !!pano.bbox[0])) return;
      footprints.push({
        kind: 'pano',
        id: pano._id,
        name: basename(pano.filename),
        stem: basename(pano.filename),
        bounds: pano.bbox as [number, number, number, number],
        meta: { createdAt: pano.createdAt },
      });
    });
    return footprints;
  },
);

const selectHiddenRasterStems = (state: RootState) => state.map.common.hiddenRasterStems;

// A tile hidden by the Orthomosaic tree is absent from the MAP entirely - no
// outline, no hover, no click (D56): the pane is where hidden tiles live until
// re-shown. Pane consumers keep getTifFootprints (all tiles).
export const getVisibleTifFootprints = createSelector(
  [getTifFootprints, selectHiddenRasterStems],
  (footprints, hidden): FileFootprint[] => (
    hidden.length ? footprints.filter((fp) => !hidden.includes(fp.stem)) : footprints
  ),
);

export const getVisibleAllFootprints = createSelector(
  [getVisibleTifFootprints, getLasFootprints, getPanoFootprints],
  (tifs, las, panos): FileFootprint[] => [...tifs, ...las, ...panos],
);

const selectSelectedTifIds = (state: RootState) => state.fileSelection.selected.tif;
const selectHovered = (state: RootState) => state.fileSelection.hovered;
const selectHiddenFootprintKinds = (state: RootState) => state.map.common.hiddenFootprintKinds;

const NO_FADED_STEMS: string[] = [];

// Focus rule (D46): while any tif is selected or hovered (map or sidebar),
// every tif WITHOUT focus fades its raster (MapRasterLayer raster-opacity);
// imagery is never tinted, only dimmed. LAS focus never dims tifs. The fade
// is the tifs' map indicator, so it follows the switch card's kind filter
// (D80): Imagery unchecked means sidebar selection shows nothing on the map,
// exactly like the other kinds' boxes.
export const getFadedRasterStems = createSelector(
  [getTifFootprints, selectSelectedTifIds, selectHovered, selectHiddenFootprintKinds],
  (footprints, selectedTifIds, hovered, hiddenKinds): string[] => {
    if (hiddenKinds.includes('tif')) return NO_FADED_STEMS;
    const hoveredTifIds = (hovered?.files || [])
      .filter((f: IFileRef) => f.kind === 'tif')
      .map((f: IFileRef) => f.id);
    if (!selectedTifIds.length && !hoveredTifIds.length) return NO_FADED_STEMS;
    const faded = footprints
      .filter((fp: FileFootprint) => !selectedTifIds.includes(fp.id) && !hoveredTifIds.includes(fp.id))
      .map((fp: FileFootprint) => fp.stem);
    return faded.length ? faded : NO_FADED_STEMS;
  },
);
